import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Moment from 'moment';
import * as Yup from 'yup';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { Formik } from 'formik';
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppModal,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
  AppNoInfo,
  AppNotificationAlert,
} from '@/layout/AppComponents';
import { toFormData } from '../../../minions/forms';
import Translator from '../../../layout/components/helpers/Translator';
import { path, axiosPath } from '../../../routing/helpers/router';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import NoInfo from '../../../layout/components/helpers/NoInfo';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import ImageCropper from '@/layout/components/form/ImageCropper';

const ModuleChemicalTradingProductEditScreen = () => {
  const { moduleBridgeHex, id } = useParams();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const global = useSelector((state) => state.global);
  const [moduleData, setModuleData] = useState();
  const [product, setProduct] = useState(null);
  const [productOptions, setProductOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [productFormInitialValues, setProductFormInitialValues] = useState(
    null
  );

  // > LOAD PRODUCT //
  useEffect(() => {
    axios
      .post(axiosPath("api_r_user_module_chemicalTrading_product_get"), {
        moduleBridgeHex,
        id,
      })
      .then((r) => {
        if (isMounted.current) {
          setProduct(r.data);
          setProductFormInitialValues(r.data);
          setLoading(false);
        }
      });
  }, []);
  // < LOAD PRODUCT //

  // > LOAD PRODUCTS OPTIONS //
  useEffect(() => {
    axios
      .post(axiosPath("api_r_user_module_chemicalTrading_product_options"), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) {
          setProductOptions(r.data);
        }
      });
  }, []);
  // > LOAD PRODUCTS OPTIONS //

    // > LOAD MODULE DATA //
  useEffect(() => {
    axios
      .post(axiosPath("api_r_user_module_chemicalTrading_getModuleData"), {
        moduleBridgeHex,
      })
      .then((r) => {
        if (isMounted.current) setModuleData(r.data);
      });
  }, []);
  // > LOAD MODULE DATA //

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    comercialName: Yup.string().required(),
    chemicalName: Yup.string().required(),
    containerType: Yup.number()
      .nullable()
      .required(),
    quantity: Yup.number()
      .nullable()
      .required(),
    expireDate: Yup.string().required(),
    saleReason: Yup.string().required(),
  });
  // < FORM SCHEMA

  // < SAVE FORM
  const editProduct = async (values) => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_chemicalTrading_product_edit"),
        toFormData({
          ...values,
          moduleBridgeHex,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (err) {}
  };

  const handleSaveProduct = (values) => {
    setIsSaving(true);
    editProduct(values)
      .then(() => {
        if (isMounted.current) {
          navigate(
              path({
                name: 'user_module_chemicalTrading_product_list',
                params: { moduleBridgeHex },
              })
          );
          AppNotificationAlert({
            title: 'Completado',
            message: 'Se ha editado el producto',
            type: 'success',
            duration: 1000,
          });
        }
      })
      .catch(() => {
        AppNotificationAlert({
          title: 'Error',
          message: 'Se ha producido un error',
          type: 'danger',
          duration: 1000,
        });

        if (isMounted.current) setIsSaving(false);
      });
  };

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt))
      setFieldValue(
        'deletedFiles',
        deletedFiles.filter((s) => s !== salt)
      );
    else setFieldValue('deletedFiles', [...deletedFiles, salt]);
  };
  // < SAVE FORM

  // < TOGGLE ARCHIVE PRODUCT
  const unArchive = async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_chemicalTrading_product_unarchive"),
        { id, moduleBridgeHex }
      );
      return response.data;
    } catch (err) {}
  };

  const handleUnarchive = () => {
    unArchive()
      .then(() => {
        if (isMounted.current) {
          navigate(
              path({
                name: 'user_module_chemicalTrading_product_list',
                params: { moduleBridgeHex },
              })
          );
          AppNotificationAlert({
            title: 'Completado',
            message: 'Producto desarchivado',
            type: 'success',
            duration: 1000,
          });
        }
      })
      .catch(() => {
        AppNotificationAlert({
          title: 'Error',
          message: 'Se ha producido un error',
          type: 'danger',
          duration: 1000,
        });
      });
  };
  // < TOGGLE ARCHIVE PRODUCT

  // < ARCHIVE FORM
  const archive = async (values) => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_chemicalTrading_product_archive"),
        { ...values, id, moduleBridgeHex }
      );
      return response.data;
    } catch (err) {}
  };

  const handleArchiveForm = (values) => {
    setIsSaving(true);
    archive(values)
      .then(() => {
        if (isMounted.current) {
          setIsSaving(false);
          setShowArchiveModal(false);
          navigate(
              path({
                name: 'user_module_chemicalTrading_product_list',
                params: { moduleBridgeHex },
              })
          );
          AppNotificationAlert({
            title: 'Completado',
            message: 'Se ha archivado el producto',
            type: 'success',
            duration: 1000,
          });
        }
      })
      .catch(() => {
        AppNotificationAlert({
          title: 'Error',
          message: 'Se ha producido un error',
          type: 'danger',
          duration: 1000,
        });

        if (isMounted.current) setIsSaving(false);
      });
  };

  const validationArchiveSchema = Yup.object().shape({
    isSatisfied: Yup.number()
      .nullable()
      .test('empty-is-satisfied', '', (value) => value === 0 || value === 1),
  });
  // < ARCHIVE FORM

  const archiveBtns = () => {
    if (!product?.isArchived) {
      return (
        <span
          role="button"
          className="btn btn-danger btn-sm float-right"
          onClick={() => setShowArchiveModal(true)}
        >
          {ReactDOMServer.renderToString(
            <Translator file="module-chemicalTrading" name="archive" />
          )}
        </span>
      );
    }
    if (product?.isArchived && product?.isSatisfied == null) {
      return (
        <>
          <span
            role="button"
            className="btn btn-danger btn-sm float-right"
            onClick={handleUnarchive}
          >
            {ReactDOMServer.renderToString(
              <Translator file="module-chemicalTrading" name="unarchive" />
            )}
          </span>
          <span
            role="button"
            className="btn btn-warning btn-sm mr-1 float-right"
            onClick={() => setShowArchiveModal(true)}
          >
            {ReactDOMServer.renderToString(
              <Translator
                file="module-chemicalTrading"
                name="rate.experience"
              />
            )}
          </span>
        </>
      );
    }
    if (product?.isArchived && product?.isSatisfied !== null) {
      return (
        <span className="btn btn-danger btn-sm float-right">
          {ReactDOMServer.renderToString(
            <Translator file="module-chemicalTrading" name="archived.product" />
          )}
        </span>
      );
    }
  };

    if (!moduleData)
      return <AppNoInfo />

  return (
    <div className="pl-2">
      {!loading && (
        <BreadcrumbHandler
          paths={[
            {
              name: `${moduleData.moduleName} - ${ReactDOMServer.renderToString(
                <Translator
                  file="module-chemicalTrading"
                  name="products.list"
                />
              )}`,
              route: {
                name: 'user_module_chemicalTrading_product_list',
                params: { moduleBridgeHex },
              },
            },
            { name: product ? product.comercialName : '' },
          ]}
        />
      )}
      <section className="content">
        {product && (
          <div className="row">
            <div className="col-lg-12 mt-3">{archiveBtns()}</div>
          </div>
        )}
        <div className="row mt-3">
          <div className="col-12">
            <Card className="card" isArchived={product?.isArchived}>
              <div className="card-body">
                {loading ? (
                  <NoInfo icon={<SpinnerLoader size={40} />} />
                ) : product ? (
                  <Formik
                    validateOnChange={false}
                    validateOnBlur={false}
                    enableReinitialize
                    initialValues={productFormInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSaveProduct}
                  >
                    {({ setFieldValue, handleSubmit, values, errors }) => (
                      <>
                        {product && (
                          <>
                            <div className="row">
                              <div className="col-lg-12 mb-4">
                                {`ID: ${product.id}`}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-md-6 mb-4">
                                <ImageCropper
                                  cropShape="rect"
                                  image={values.image}
                                  width={200}
                                  height={200}
                                  onSave={(image) =>{
                                    setFieldValue('image', image)}
                                  }
                                  disabled={product?.isArchived}
                                />
                              </div>
                              <div className="col-md-6">
                                <div className="row">
                                  <div className="col-lg-12">
                                    <AppInput
                                      icon={
                                        <AppRemixIcon
                                          icon="font-color"
                                          noVariants
                                        />
                                      }
                                      title={
                                        <Translator
                                          file="module-chemicalTrading"
                                          name="comercial.name"
                                        />
                                      }
                                      onChange={(value) =>
                                        setFieldValue('comercialName', value)
                                      }
                                      value={values.comercialName}
                                      error={!!errors.comercialName}
                                      readOnly={product?.isArchived}
                                      isClearable
                                      required
                                    />
                                  </div>
                                  <div className="col-lg-12">
                                    <AppInput
                                      icon={
                                        <AppRemixIcon
                                          icon="font-color"
                                          noVariants
                                        />
                                      }
                                      title={
                                        <Translator
                                          file="module-chemicalTrading"
                                          name="chemical.name"
                                        />
                                      }
                                      onChange={(value) =>
                                        setFieldValue('chemicalName', value)
                                      }
                                      value={values.chemicalName}
                                      error={!!errors.chemicalName}
                                      readOnly={product?.isArchived}
                                      isClearable
                                      required
                                    />
                                  </div>
                                  <div className="col-lg-12">
                                    <AppSingleSelect
                                      options={productOptions?.containerTypes}
                                      icon={<AppRemixIcon icon="apps-2" />}
                                      title={ReactDOMServer.renderToString(
                                        <Translator
                                          file="module-chemicalTrading"
                                          name="container.type"
                                        />
                                      )}
                                      onChange={(value) =>
                                        setFieldValue('containerType', value)
                                      }
                                      value={values.containerType}
                                      error={!!errors.containerType}
                                      readOnly={product?.isArchived}
                                      isClearable
                                      required
                                    />
                                  </div>
                                  <div className="col-lg-12">
                                    <AppInputNumber
                                      title={ReactDOMServer.renderToString(
                                        <Translator
                                          file="module-chemicalTrading"
                                          name="quantity"
                                        />
                                      )}
                                      icon={
                                        <AppRemixIcon
                                          icon="hashtag"
                                          noVariants
                                        />
                                      }
                                      value={values.quantity}
                                      onChange={(value) =>
                                        setFieldValue('quantity', value)
                                      }
                                      error={!!errors.quantity}
                                      readOnly={product?.isArchived}
                                      isClearable
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <AppDatePicker
                                  title={
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="expire.date"
                                    />
                                  }
                                  value={values.expireDate}
                                  onChange={(date) =>
                                    setFieldValue('expireDate', date)
                                  }
                                  error={!!errors.expireDate}
                                  readOnly={product?.isArchived}
                                  showMonthDropdown
                                  showYearDropdown
                                  isClearable
                                  required
                                />
                              </div>
                              <div className="col-md-6">
                                <AppDatePicker
                                  title={
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="close.date"
                                    />
                                  }
                                  value={values.closeDate}
                                  onChange={(date) =>
                                    setFieldValue('closeDate', date)
                                  }
                                  maxDate={
                                    product &&
                                    Moment(
                                      product.lastEditionDate,
                                      'DD/MM/YYYY'
                                    )
                                      .add(1, 'year')
                                      .toDate()
                                  }
                                  readOnly={product?.isArchived}
                                  showMonthDropdown
                                  showYearDropdown
                                  isClearable
                                  required
                                />
                              </div>
                              <div className="col-md-6">
                                <AppTextArea
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="sale.reason"
                                    />
                                  )}
                                  onChange={(v) =>
                                    setFieldValue('saleReason', v)
                                  }
                                  value={values.saleReason}
                                  error={!!errors.saleReason}
                                  readOnly={product?.isArchived}
                                  isClearable
                                  required
                                />
                              </div>
                              <div className="col-md-6">
                                <AppTextArea
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="contact.data"
                                    />
                                  )}
                                  onChange={(v) =>
                                    setFieldValue('contactData', v)
                                  }
                                  value={values.contactData}
                                  readOnly={product?.isArchived}
                                  isClearable
                                />
                              </div>
                              <div className="col-md-6">
                                <AppTextArea
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="product.use"
                                    />
                                  )}
                                  onChange={(v) =>
                                    setFieldValue('productUse', v)
                                  }
                                  value={values.productUse}
                                  error={!!errors.productUse}
                                  readOnly={product?.isArchived}
                                  isClearable
                                />
                              </div>
                              <div className="col-md-6">
                                <AppTextArea
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="features"
                                    />
                                  )}
                                  onChange={(v) => setFieldValue('features', v)}
                                  value={values.features}
                                  error={!!errors.features}
                                  readOnly={product?.isArchived}
                                  isClearable
                                />
                              </div>
                              <div className="col-md-6">
                                <AppInput
                                  icon={<AppRemixIcon icon="map-pin" />}
                                  title={
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="store.place"
                                    />
                                  }
                                  onChange={(value) =>
                                    setFieldValue('storePlace', value)
                                  }
                                  value={values.storePlace}
                                  readOnly={product?.isArchived}
                                  isClearable
                                />
                              </div>
                              <div className="col-md-6">
                                <AppInput
                                  icon={<AppRemixIcon icon="alert" />}
                                  title={
                                    <Translator
                                      file="module-chemicalTrading"
                                      name="risk"
                                    />
                                  }
                                  onChange={(value) =>
                                    setFieldValue('risk', value)
                                  }
                                  value={values.risk}
                                  readOnly={product?.isArchived}
                                  isClearable
                                />
                              </div>
                              <div className="col-md-6">
                                <label className="col-form-label mr-3 pointer">
                                  <input
                                    type="checkbox"
                                    className="mr-1"
                                    value={values.isCompanyName}
                                    onChange={() =>
                                      !product?.isArchived
                                        ? setFieldValue(
                                            'isCompanyName',
                                            !values.isCompanyName
                                          )
                                        : null
                                    }
                                    checked={values.isCompanyName}
                                  />
                                  <Translator
                                    file="module-chemicalTrading"
                                    name="show.company.name"
                                  />
                                </label>
                              </div>
                              <div className="col-md-6">
                                <label className="col-form-label mr-3 pointer">
                                  <input
                                    type="checkbox"
                                    className="mr-1"
                                    value={values.isRepackable}
                                    onChange={() =>
                                      !product?.isArchived
                                        ? setFieldValue(
                                            'isRepackable',
                                            !values.isRepackable
                                          )
                                        : null
                                    }
                                    checked={values.isRepackable}
                                  />
                                  <Translator
                                    file="module-chemicalTrading"
                                    name="is.repackable"
                                  />
                                </label>
                              </div>
                              <div className="col-lg-12 mt-4">
                                <label>
                                  <Translator
                                    file="module-chemicalTrading"
                                    name="files"
                                  />
                                </label>
                                {!product?.isArchived && (
                                  <AppFileUploader
                                    isMulti
                                    placeholder={
                                      !values.files || values.files.length === 0
                                        ? 'Selecciona uno o varios archivos'
                                        : `${values.files.length} archivos seleccionados`
                                    }
                                    onChange={(opt) =>
                                      setFieldValue('files', opt)
                                    }
                                    btnText={
                                      <Translator
                                        file="general"
                                        name="search..."
                                      />
                                    }
                                  />
                                )}
                              </div>
                              {values.id &&
                                product.files &&
                                Object.keys(product.files).length > 0 && (
                                  <div className="col-lg-12">
                                    <div className="row">
                                      <div className="col-lg-12 mt-4">
                                        <label>
                                          <Translator
                                            file="module-chemicalTrading"
                                            name="delete.files"
                                          />
                                        </label>
                                        <div>
                                          {Object.keys(values.files).map(
                                            (salt) => (
                                              <div key={salt} className="mt-2">
                                                <a
                                                  target="_blank"
                                                  title={
                                                    product.files[salt].name
                                                  }
                                                  className="mr-3"
                                                  href={`${global.urlData.userApiUrl}m/chemical-trading/get-product-file/${moduleBridgeHex}/${product.id}/${salt}`}
                                                  rel="noreferrer"
                                                >
                                                  <small>Descargar</small>
                                                </a>
                                                {!product?.isArchived && (
                                                  <label className="mb-2 pointer font-weight-light">
                                                    <input
                                                      type="checkbox"
                                                      className="mr-1"
                                                      value={salt}
                                                      onChange={() =>
                                                        handleDeletedFiles(
                                                          setFieldValue,
                                                          values.deletedFiles,
                                                          salt
                                                        )
                                                      }
                                                      checked={
                                                        values.deletedFiles &&
                                                        values.deletedFiles[
                                                          salt
                                                        ]
                                                      }
                                                    />
                                                    {values.files[salt].name}
                                                  </label>
                                                )}
                                              </div>
                                            )
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                            </div>
                            {product && !product?.isArchived && (
                              <div className="row">
                                <div className="col-12 text-right">
                                  <button
                                    className="btn btn-success mt-4"
                                    type="submit"
                                    onClick={handleSubmit}
                                    disabled={isSaving}
                                  >
                                    <Translator
                                      file="module-chemicalTrading"
                                      name={id ? 'edit' : 'add'}
                                    />
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Formik>
                ) : (
                  <>El producto no existe</>
                )}
              </div>
            </Card>
          </div>
        </div>
        <AppModal
          show={showArchiveModal}
          title={
            product?.isArchived && !product?.isSatisfied ? (
              <Translator
                file="module-chemicalTrading"
                name="rate.experience"
              />
            ) : (
              <Translator
                file="module-chemicalTrading"
                name="archive.product"
              />
            )
          }
          onClose={() => setShowArchiveModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
            initialValues={{
              isSatisfied: -1,
            }}
            validationSchema={validationArchiveSchema}
            onSubmit={handleArchiveForm}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <>
                <div className="row">
                  <div className="col-lg-12 text-center">
                    <h5>
                      ¿Ha sido satisfactoria la gestión del producto a través de
                      la plataforma?
                    </h5>
                  </div>
                </div>
                <div className="row">
                  <SatisfiedWrapper
                    className="col-sm-6 text-center"
                    color={values.isSatisfied === 0 ? '#ffb9b9' : 'white'}
                  >
                    <RadioInput
                      type="radio"
                      name="isSatisfied"
                      id="unsatisfied"
                      checked={values.isSatisfied}
                      value={values.isSatisfied}
                      onChange={() => setFieldValue('isSatisfied', 0)}
                    />
                    <SatisfiedLabel htmlFor="unsatisfied">
                      <AppRemixIcon icon="emotion-unhappy" color="red" />
                    </SatisfiedLabel>
                  </SatisfiedWrapper>
                  <SatisfiedWrapper
                    className="col-sm-6 text-center"
                    color={values.isSatisfied === 1 ? '#ccffb9' : 'white'}
                  >
                    <RadioInput
                      type="radio"
                      name="isSatisfied"
                      id="satisfied"
                      checked={values.isSatisfied}
                      value={values.isSatisfied}
                      onChange={() => setFieldValue('isSatisfied', 1)}
                    />
                    <SatisfiedLabel htmlFor="satisfied">
                      <AppRemixIcon icon="user-smile" color="green" />
                    </SatisfiedLabel>
                  </SatisfiedWrapper>
                </div>
                <div className="row">
                  <div className="col-12 text-right">
                    <button
                      className={`btn btn-${
                        product?.isArchived && !product?.isSatisfied
                          ? 'warning'
                          : 'danger'
                      } mt-2`}
                      type="submit"
                      onClick={handleSubmit}
                      disabled={isSaving || values.isSatisfied === -1}
                    >
                      <Translator
                        file="module-chemicalTrading"
                        name={
                          product?.isArchived && !product?.isSatisfied
                            ? 'rate.experience'
                            : 'archive'
                        }
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </AppModal>
      </section>
    </div>
  );
};

export default ModuleChemicalTradingProductEditScreen;

const Card = styled.div`
  border: ${(props) => (props.isArchived ? '1px solid red' : 'border: none')};
`;

const RadioInput = styled.input`
  opacity: 0;
`;

const SatisfiedWrapper = styled.div`
  background-color: ${(props) => props.color};
`;

const SatisfiedLabel = styled.label`
  font-size: 120px;
  color: ${(props) => props.color};
  &:hover {
    cursor: pointer;
  }
`;
